import { useEffect, useRef, useState } from 'react';
import { PlayIcon } from '../components/icons';
import { cx } from '../components/ui';
import type { BrandSlate } from '../lib/types';

/**
 * A team's intro or outro, played in the frame the recording is about to
 * appear in (or has just left).
 *
 * Deliberately its own element rather than a source swapped into the main
 * player. The main player carries a transcript, reactions pinned to seconds,
 * a view beacon and an edit timeline, all of which are about the recording;
 * pushing a second video through it would make every one of those lie about
 * what is on screen. A separate element for a few seconds of branding keeps
 * all of that exactly as true as it was.
 *
 * Always skippable. An intro nobody can get past is the thing people hate
 * about branded video, and a viewer who skips it has still seen it.
 */
export function SlateClip({
  slate,
  /** 'intro' waits for a press; 'outro' follows the recording and just plays. */
  role,
  /** Drawn over the first frame while the intro waits: whose recording this is. */
  title,
  onDone,
}: {
  slate: BrandSlate;
  role: 'intro' | 'outro';
  title?: string;
  onDone: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [started, setStarted] = useState(role === 'outro');

  // The outro follows a video the viewer was already watching, so the gesture
  // that started that one covers this one too and it may simply play. A
  // browser that refuses anyway must not strand the viewer on a still frame,
  // so a refusal moves straight on to what comes next.
  useEffect(() => {
    if (role !== 'outro') return;
    const video = videoRef.current;
    if (!video) return;
    void video.play().catch(() => onDone());
  }, [role, onDone]);

  const start = () => {
    const video = videoRef.current;
    if (!video) return;
    setStarted(true);
    void video.play().catch(() => onDone());
  };

  return (
    <div className="group relative overflow-hidden rounded-md border border-hairline bg-black">
      <video
        ref={videoRef}
        playsInline
        preload="auto"
        src={slate.mp4Url}
        poster={slate.posterUrl ?? undefined}
        className="aspect-video w-full bg-black"
        onEnded={onDone}
        // A slate that will not play is not worth stopping a viewer over.
        onError={onDone}
      />

      {!started ? (
        <button
          type="button"
          onClick={start}
          aria-label={title ? `Play ${title}` : 'Play'}
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-950/35 transition-colors duration-120 hover:bg-slate-950/45"
        >
          <span className="brand-play flex h-16 w-16 items-center justify-center rounded-full">
            <PlayIcon size={26} />
          </span>
          {title ? <span className="max-w-md px-6 text-center text-body text-chalk">{title}</span> : null}
        </button>
      ) : null}

      <button
        type="button"
        onClick={onDone}
        className={cx(
          'absolute bottom-3 right-3 rounded border border-white/25 bg-slate-950/70 px-2.5 py-1 text-caption text-chalk',
          'transition-opacity duration-200 hover:bg-slate-950/90',
          started ? 'opacity-100' : 'opacity-0',
        )}
      >
        {role === 'intro' ? 'Skip intro' : 'Skip'}
      </button>
    </div>
  );
}
